import {
  signalStoreFeature,
  withMethods,
  patchState,
  withProps,
} from '@ngrx/signals';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, exhaustMap, filter, tap, Observable } from 'rxjs';
import {
  EntityId,
  removeEntity,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import {
  withRequestStatus,
  setError,
  setReadStatus,
  setDeleteStatus,
} from './request-status.feature';
import { StateStatus } from '../../app/core/enums/state-status.enum';
import { SharedUIService } from '../services/ui-handler.service';
import { IConfirmDialogData } from '../interfaces';
import { UNDO_CLICKED } from '../constants';
import { ComponentType } from '@angular/cdk/portal';
import { inject } from '@angular/core';
import { withUndoRedo } from '@angular-architects/ngrx-toolkit';
import { HttpErrorResponse } from '@angular/common/http';

type DialogWorkflowConfig<TDialogResult, TServiceResult> = {
  dialogComponent: ComponentType<unknown>;
  dialogConfig?: Record<string, unknown>;
  getServiceCall: (dialogResult: TDialogResult) => Observable<TServiceResult>;
  onSuccess: (result: TServiceResult) => void;
  withUndo?: boolean;
  undoMessage?: string;
  onBeforeServiceCall?: (dialogResult: TDialogResult) => void;
};

export function withCRUD<T extends { id: EntityId }>() {
  return signalStoreFeature(
    withEntities<T>(),
    withRequestStatus(),
    withUndoRedo(),
    withProps(() => ({
      _sharedUIService: inject(SharedUIService),
    })),
    withMethods((store) => {
      function loadRxMethod<U = void>() {
        return (
          serviceCall: () => Observable<T[]>,
          options?: {
            beforeLoad?: () => void;
            afterLoad?: (entities: T[]) => void;
          },
        ) => {
          return rxMethod<U>(
            pipe(
              tap(() => options?.beforeLoad?.()),
              exhaustMap(() =>
                serviceCall().pipe(
                  tapResponse({
                    next: (entities) => {
                      patchState(
                        store,
                        setAllEntities(entities),
                        setReadStatus(StateStatus.FULFILLED),
                      );
                      options?.afterLoad?.(entities);
                    },
                    error: (error: HttpErrorResponse) =>
                      patchState(store, setError(error.error.message)),
                  }),
                ),
              ),
            ),
          );
        };
      }

      function deleteRxMethod<T extends { id: string }>() {
        return (
          serviceCall: (id: string) => Observable<unknown>,
          successMessage: string,
          deleteDialogData?: IConfirmDialogData,
          deleteDialogDataFn?: (entity: T) => IConfirmDialogData,
          onUndo?: (entity: T) => void,
        ) => {
          return rxMethod<T>(
            pipe(
              tap(() =>
                patchState(store, setDeleteStatus(StateStatus.PENDING)),
              ),
              exhaustMap((entity) =>
                store._sharedUIService
                  .openConfirmDialog(
                    deleteDialogDataFn?.(entity) || deleteDialogData,
                  )
                  .pipe(
                    filter((confirmed) => !!confirmed),
                    exhaustMap(() =>
                      store._sharedUIService
                        .withUndoRequestAction(
                          serviceCall(entity.id),
                          successMessage,
                        )
                        .pipe(
                          tapResponse({
                            next: (result) => {
                              if (result === UNDO_CLICKED) {
                                if (onUndo) {
                                  onUndo(entity);
                                }
                                patchState(
                                  store,
                                  setDeleteStatus(StateStatus.FULFILLED),
                                );
                              } else {
                                patchState(
                                  store,
                                  removeEntity(entity.id),
                                  setDeleteStatus(StateStatus.FULFILLED),
                                );
                              }
                            },
                            error: (error: HttpErrorResponse) =>
                              patchState(store, setError(error.error.message)),
                          }),
                        ),
                    ),
                  ),
              ),
            ),
          );
        };
      }

      function runDialogWorkflow<TDialogResult, TServiceResult>(
        sharedUI: SharedUIService,
        config: DialogWorkflowConfig<TDialogResult, TServiceResult>,
      ): Observable<TServiceResult | typeof UNDO_CLICKED> {
        const {
          dialogComponent,
          dialogConfig,
          getServiceCall,
          onSuccess,
          withUndo,
          undoMessage,
          onBeforeServiceCall,
        } = config;

        const dialogRef = sharedUI.openDialog(dialogComponent, dialogConfig);

        return dialogRef.afterClosed().pipe(
          filter((result) => !!result),
          tap((result) => {
            if (onBeforeServiceCall) {
              onBeforeServiceCall(result);
            }
          }),
          exhaustMap((result) => {
            const call$ = withUndo
              ? sharedUI.withUndoRequestAction(
                  getServiceCall(result),
                  undoMessage!,
                )
              : getServiceCall(result);

            return call$.pipe(
              tapResponse({
                next: (res) => {
                  if (res !== false) {
                    if (res === UNDO_CLICKED) {
                      store.undo();
                    } else {
                      onSuccess(res);
                    }
                  }
                },
                error: (error: HttpErrorResponse) =>
                  patchState(store, setError(error.error.message)),
              }),
            );
          }),
        );
      }

      return {
        deleteRxMethod,
        loadRxMethod,
        runDialogWorkflow,
      };
    }),
  );
}
